/**
 * DirtCanvas — Canvas-based scratch-off dirt layer.
 *
 * Fills the canvas with a brown dirt texture, then erases where the player
 * drags using globalCompositeOperation = 'destination-out'.
 *
 * Purely cosmetic — no game logic. Reports dig positions to parent via callback.
 */

import { useRef, useEffect, useCallback } from 'react';

interface DirtCanvasProps {
  width: number;
  height: number;
  brushRadius: number;
  onDig: (x: number, y: number) => void;
  enabled: boolean;
}

/** Draw procedural dirt texture on the canvas */
function drawDirtTexture(ctx: CanvasRenderingContext2D, w: number, h: number) {
  // Base dirt color
  ctx.fillStyle = '#6B4226';
  ctx.fillRect(0, 0, w, h);

  // Random darker specks for texture
  const speckCount = Math.floor((w * h) / 120);
  for (let i = 0; i < speckCount; i++) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const size = 1 + Math.random() * 3;
    const shade = Math.floor(Math.random() * 40);
    ctx.fillStyle = `rgb(${87 - shade}, ${50 - shade}, ${20 - shade})`;
    ctx.fillRect(x, y, size, size);
  }

  // Lighter soil patches
  const patchCount = Math.floor((w * h) / 2000);
  for (let i = 0; i < patchCount; i++) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const radius = 3 + Math.random() * 8;
    ctx.fillStyle = `rgba(139, 90, 43, ${0.3 + Math.random() * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Tiny pebble-like dots
  const pebbleCount = Math.floor((w * h) / 800);
  for (let i = 0; i < pebbleCount; i++) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const r = 1 + Math.random() * 2;
    ctx.fillStyle = `rgba(160, 130, 100, ${0.2 + Math.random() * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function DirtCanvas({ width, height, brushRadius, onDig, enabled }: DirtCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDiggingRef = useRef(false);
  const lastDigPosRef = useRef<{ x: number; y: number } | null>(null);

  // Draw initial dirt texture
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0 || height === 0) return;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawDirtTexture(ctx, width, height);
  }, [width, height]);

  const erase = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Erase a circle at the dig point
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, brushRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';

      // Interpolate between last position and current to avoid gaps
      const last = lastDigPosRef.current;
      if (last) {
        const dx = x - last.x;
        const dy = y - last.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const step = brushRadius * 0.4;
        if (dist > step) {
          const steps = Math.ceil(dist / step);
          ctx.globalCompositeOperation = 'destination-out';
          for (let i = 1; i < steps; i++) {
            const t = i / steps;
            const ix = last.x + dx * t;
            const iy = last.y + dy * t;
            ctx.beginPath();
            ctx.arc(ix, iy, brushRadius, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalCompositeOperation = 'source-over';
        }
      }

      lastDigPosRef.current = { x, y };
      onDig(x, y);
    },
    [brushRadius, onDig],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      isDiggingRef.current = true;
      lastDigPosRef.current = null;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      erase(e.clientX, e.clientY);
    },
    [enabled, erase],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDiggingRef.current || !enabled) return;
      erase(e.clientX, e.clientY);
    },
    [enabled, erase],
  );

  const handlePointerUp = useCallback(() => {
    isDiggingRef.current = false;
    lastDigPosRef.current = null;
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 cursor-pointer"
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    />
  );
}
